import { Component, Prop, State, Method, Element, h, Host, Build } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-form-type-1';

const ANIMATION_DURATION = 250;

interface CompanySizeOption {
  value: string;
  label: string;
}

interface HiddenField {
  name?: string;
  type?: string;
  value?: string;
}

interface FieldMessage {
  field?: string;
  requiredMessage?: string;
  invalidMessage?: string;
}

const DEFAULT_COMPANY_SIZES: CompanySizeOption[] = [
  { value: '5', label: '0 - 5' },
  { value: '50', label: '6 - 50' },
  { value: '200', label: '51 - 200' },
  { value: '1000', label: '201 - 1000' },
  { value: '2000', label: '1001 - 2000' },
  { value: '2001', label: '2001+' },
];

/**
 * "Request a demo" lead-capture modal dialog with a centered card form.
 * A trigger button opens an overlay that fades in while the card slides up; the card holds a centered title/subtitle header, a close button, and a multi-field contact form (name row, business email, phone with dial-code prefix, company, job title + company-size row, and an optional message), a full-width submit button and an agreement note. On mobile the card fills the screen width; on tablet and up it becomes a 608px-wide centered card with generous padding.
 * Unlike inline form sections, this type renders a dismissible modal (overlay click, close button, or Escape) with body scroll-locking and slide/fade open-close animations.
 *
 * @category SignNow
 * @slot trigger - Custom element(s) that open the modal when clicked. Falls back to a button rendered from `trigger-text` when empty.
 * @example
 *   <sn-form-type-1
 *     trigger-text="Request a demo"
 *     heading="Get connected with us"
 *     subtitle="See for yourself why millions of users trust airSlate SignNow to go paperless."
 *     submit-text="Request a demo"
 *     action="/sales/solutions-icp"
 *     redirect-url="https://www.signnow.com/thankyou"
 *     agreement-note='By clicking "Request a demo" I agree to receive marketing communications from airSlate SignNow in accordance with the <a href="/terms">Terms of Service</a> and <a href="/privacy_notice">Privacy Notice</a>.'
 *   ></sn-form-type-1>
 */
@Component({
  tag: 'sn-form-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnFormType1 {
  @Element() el!: HTMLElement;

  /**
   * Modal heading shown at the top of the card.
   * @content
   */
  @Prop() heading = 'Get connected with us';

  /**
   * Supporting paragraph under the heading. Supports inline HTML.
   * @content
   */
  @Prop() subtitle =
    'See for yourself why millions of users trust airSlate SignNow to go paperless and manage eSignature workflows faster and more securely. Fill out the form to schedule a free demo customized for your specific company’s needs. Once you’ve finished, we’ll be in touch.';

  /**
   * Label for the built-in trigger button. Leave empty to rely solely on the `trigger` slot or the public `open()` method.
   * @content
   */
  @Prop() triggerText = 'Request a demo';

  /**
   * Visual variant for the built-in fallback trigger button (`me-button` variant).
   * @default 'primary'
   */
  @Prop({ attribute: 'trigger-variant' }) triggerVariant: 'primary' | 'secondary' | 'bordered' | 'tertiary' | 'detailed' | 'ghost' = 'primary';

  /**
   * Label for the form submit button.
   * @content
   */
  @Prop() submitText = 'Request a demo';

  /**
   * Small helper line above the fields.
   * @content
   */
  @Prop() asteriskNote = 'Asterisk denotes mandatory fields (*)';

  /**
   * Legal/marketing consent note rendered under the submit button. Supports inline HTML.
   * @content
   */
  @Prop() agreementNote =
    'By clicking "Request a demo" I agree to receive marketing communications from airSlate SignNow in accordance with the <a href="https://www.signnow.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://www.signnow.com/privacy_notice" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.';

  /** Form `action` URL the demo request is submitted to. */
  @Prop() action = '#';

  /**
   * URL the user is redirected to after a successful form submission. When omitted, no redirect occurs.
   */
  @Prop({ attribute: 'redirect-url' }) redirectUrl?: string;

  /**
   * Extra hidden form fields the visual form doesn't expose. Accepts either a real array
   * (set via JS property, e.g. Storybook `.hidden-fields=` or a parent component's
   * `hiddenFields={array}`) or a JSON string of `{ "name": string, "type"?: string, "value"?: string }`
   * objects (set via the `hidden-fields` HTML attribute / Herald). It is normalized to an
   * array in `componentWillLoad` and rendered as `<input type="hidden">` inside the lead
   * form so the fields post alongside the visible ones. The `| string` union is required so
   * Stencil observes the `hidden-fields` attribute.
   *
   * Used by the backend page migrator to inject values the SignNow landing expects
   * (e.g. `industry_name`, `_token`, `gclid`). Static per-page values (like
   * `industry_name`) are set here directly; per-request dynamic values (`_token`,
   * `gclid`) are populated by the backend/runtime — this repo only renders the fields.
   *
   * @example [{"name":"industry_name","value":"comparison"},{"name":"_token","value":""},{"name":"gclid","value":""}]
   * @type {array}
   * @hiddenFields.type {object}
   * @hiddenFields.property {string} name - Field name posted with the form (e.g. "gclid", "industry_name")
   * @hiddenFields.property {string} type - Native input type for the rendered field; defaults to "hidden"
   * @hiddenFields.property {string} value - Value posted for the field; may be empty (backend/runtime fills dynamic ones)
   * @hiddenFields.required name
   */
  @Prop({ mutable: true, attribute: 'hidden-fields' }) hiddenFields?: HiddenField[] | string;

  /**
   * Per-field override for validation error text. Each entry targets a form field by its
   * `field` name and replaces that field's default `required` / `invalid` message. Fields
   * left unset keep their built-in defaults, so existing forms are unchanged.
   * Valid field names: `first_name`, `last_name`, `email`, `phone`, `company_name`, `job_title`, `users`.
   * Accepts a real array or a JSON string (HTML attribute / Herald).
   * @example [{"field":"email","requiredMessage":"Please enter your work email","invalidMessage":"That email looks off"}]
   * @type {array}
   * @fieldMessages.type {object}
   * @fieldMessages.property {string} field - Target field name (e.g. "email", "first_name")
   * @fieldMessages.property {string} requiredMessage - Error text shown when the field is left empty
   * @fieldMessages.property {string} invalidMessage - Error text shown when the value is invalid (applies to the email field)
   * @fieldMessages.required field
   */
  @Prop({ mutable: true, attribute: 'field-messages' }) fieldMessages?: FieldMessage[] | string;

  /** Static dial code shown in the phone field prefix (presentational). */
  @Prop() dialCode = '+1';

  /** Accessible label for the close button. */
  @Prop() closeLabel = 'Close modal';

  /**
   * Options for the "Company size" dropdown.
   *
   * @type {array}
   * @option.type {object}
   * @option.property {string} value - Value submitted with the form
   * @option.property {string} label - Visible option label
   * @option.required value
   * @option.required label
   */
  @Prop({ mutable: true }) companySizeOptions: CompanySizeOption[] | string = DEFAULT_COMPANY_SIZES;

  /**
   * Show the optional "Questions or comments" message textarea. When `false` the
   * field is neither rendered nor submitted.
   * @default true
   */
  @Prop({ attribute: 'show-message' }) showMessage = true;

  /**
   * Show the "Job title" text field. When `false` the field is neither rendered nor submitted.
   * @default true
   */
  @Prop({ attribute: 'show-job-title' }) showJobTitle = true;

  /**
   * Show the "Company size" dropdown. When `false` the field is neither rendered nor submitted.
   * @default true
   */
  @Prop({ attribute: 'show-company-size' }) showCompanySize = true;

  /** Open the modal on initial render (useful for previews). */
  @Prop() defaultOpen = false;

  /** Controls whether the modal is mounted/visible (`is-open`). */
  @State() isOpen = false;

  /** Mirrors `aria-hidden`; drives the enter/exit animation. */
  @State() hidden = true;

  private closeTimer?: number;
  private triggerWrapEl?: HTMLElement;
  private overlayEl?: HTMLElement;

  componentWillLoad() {
    this.companySizeOptions = parseJsonProp(this.companySizeOptions) || DEFAULT_COMPANY_SIZES;
    // Normalize `hiddenFields`: accept a JSON string (HTML attribute / Herald) or a real
    // array (JS property). Malformed input → [].
    this.hiddenFields = parseJsonProp<HiddenField[]>(this.hiddenFields, []);
    // Normalize per-field message overrides (JSON string or real array). Malformed → [].
    this.fieldMessages = parseJsonProp<FieldMessage[]>(this.fieldMessages, []);
    if (this.defaultOpen) {
      this.isOpen = true;
      this.hidden = false;
    }
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    document.addEventListener('keydown', this.onKeyDown);
    // Bind click handlers imperatively so non-interactive wrappers stay free of
    // JSX click handlers (a11y) while still supporting slotted triggers and the
    // backdrop-to-close behavior.
    this.triggerWrapEl?.addEventListener('click', this.onTriggerClick);
    this.overlayEl?.addEventListener('click', this.onOverlayClick);
    if (this.isOpen) this.lockScroll(true);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    document.removeEventListener('keydown', this.onKeyDown);
    this.triggerWrapEl?.removeEventListener('click', this.onTriggerClick);
    this.overlayEl?.removeEventListener('click', this.onOverlayClick);
    window.clearTimeout(this.closeTimer);
    this.lockScroll(false);
  }

  /** Open the modal. */
  @Method()
  async open() {
    window.clearTimeout(this.closeTimer);
    this.isOpen = true;
    // Defer aria-hidden flip to the next frame so the enter animation runs.
    requestAnimationFrame(() => {
      this.hidden = false;
    });
    this.lockScroll(true);
  }

  /** Close the modal (plays the exit animation before unmounting). */
  @Method()
  async close() {
    this.hidden = true;
    this.lockScroll(false);
    this.closeTimer = window.setTimeout(() => {
      this.isOpen = false;
    }, ANIMATION_DURATION);
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen && !this.hidden) {
      this.close();
    }
  };

  private onTriggerClick = (event: MouseEvent) => {
    event.preventDefault();
    this.open();
  };

  private onOverlayClick = (event: MouseEvent) => {
    // Close only when the click originates on the overlay/container backdrop.
    const target = event.target as HTMLElement;
    if (target.closest(`.${COMPONENT_NAME}__form-container`)) return;
    this.close();
  };

  /**
   * Redirect to the thank-you page after the lead form posts successfully.
   * `form-handler-type-1` only emits `formSubmitSuccess` once the POST returns `response.ok`.
   */
  private handleSubmitSuccess = () => {
    if (!Build.isBrowser || !this.redirectUrl) return;
    window.location.href = this.redirectUrl;
  };

  private lockScroll(locked: boolean) {
    if (!Build.isBrowser) return;
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /**
   * Render the normalized `hiddenFields` as hidden `<input>` elements. Returns `null`
   * when there are none, so a malformed or empty value can never break the form.
   */
  /**
   * Resolve the error text for a field: a matching `fieldMessages` override wins,
   * otherwise the built-in default is used (so unset fields behave exactly as before).
   */
  private fieldMsg(field: string, kind: 'requiredMessage' | 'invalidMessage', fallback: string): string {
    const list = Array.isArray(this.fieldMessages) ? this.fieldMessages : [];
    const entry = list.find(f => f && f.field === field);
    const custom = entry && entry[kind];
    return custom && custom.trim() ? custom : fallback;
  }

  private renderHiddenFields() {
    const fields = Array.isArray(this.hiddenFields) ? this.hiddenFields : [];
    const inputs = fields.filter(field => field && field.name);
    if (inputs.length === 0) return null;
    return (
      <div class={`${COMPONENT_NAME}__hidden-fields`} hidden>
        {inputs.map(field => (
          <input type={field.type || 'hidden'} name={field.name} value={field.value ?? ''} />
        ))}
      </div>
    );
  }

  render() {
    const options = this.companySizeOptions as CompanySizeOption[];

    return (
      <Host class={COMPONENT_NAME}>
        <span class={`${COMPONENT_NAME}__trigger-wrap`} ref={el => (this.triggerWrapEl = el)}>
          <slot name="trigger">
            {this.triggerText && (
              <me-button class={`${COMPONENT_NAME}__trigger`} tag="button" variant={this.triggerVariant}>
                {this.triggerText}
              </me-button>
            )}
          </slot>
        </span>

        <div
          class={{ [`${COMPONENT_NAME}__modal`]: true, 'is-open': this.isOpen }}
          aria-hidden={this.hidden ? 'true' : 'false'}
          role="dialog"
          aria-modal="true"
          aria-label={this.heading}
        >
          <div class={`${COMPONENT_NAME}__overlay`} ref={el => (this.overlayEl = el)}>
            <div class={`${COMPONENT_NAME}__container`}>
              <div class={`${COMPONENT_NAME}__form-container`} role="document">
                <header class={`${COMPONENT_NAME}__header`}>
                  <div class={`${COMPONENT_NAME}__title`}>{this.heading}</div>
                  <div class={`${COMPONENT_NAME}__subtitle`} innerHTML={this.subtitle}></div>
                  <button
                    type="button"
                    class={`${COMPONENT_NAME}__close`}
                    aria-label={this.closeLabel}
                    onClick={() => this.close()}
                  ></button>
                </header>

                <div class={`${COMPONENT_NAME}__content`}>
                  <form-handler-type-1
                    class={`${COMPONENT_NAME}__form`}
                    action={this.action}
                    method="post"
                    disableFieldPrefix
                    encoding="urlencoded"
                    onFormSubmitSuccess={this.handleSubmitSuccess}
                  >
                    <div class={`${COMPONENT_NAME}__asterisk`}>{this.asteriskNote}</div>

                    <div class={`${COMPONENT_NAME}__row`}>
                      <me-input name="first_name" type="text" placeholder="First Name*" required required-message={this.fieldMsg('first_name', 'requiredMessage', 'Please enter your First Name')}></me-input>
                      <me-input name="last_name" type="text" placeholder="Last Name*" required required-message={this.fieldMsg('last_name', 'requiredMessage', 'Please enter your Last Name')}></me-input>
                    </div>

                    <me-input
                      name="email"
                      type="email"
                      placeholder="Business Email*"
                      required
                      required-message={this.fieldMsg('email', 'requiredMessage', 'Please enter your Business Email')}
                      invalid-message={this.fieldMsg('email', 'invalidMessage', 'This value seems to be invalid')}
                    ></me-input>

                    <div class={`${COMPONENT_NAME}__phone`}>
                      {/*<span class={`${COMPONENT_NAME}__phone-prefix`} aria-hidden="true">*/}
                      {/*  <span class={`${COMPONENT_NAME}__phone-flag`}></span>*/}
                      {/*  <span class={`${COMPONENT_NAME}__phone-code`}>{this.dialCode}</span>*/}
                      {/*</span>*/}
                      <me-input name="phone" type="tel" placeholder="Phone number*" required required-message={this.fieldMsg('phone', 'requiredMessage', 'Please enter your Phone number')}></me-input>
                    </div>

                    <me-input name="company_name" type="text" placeholder="Company name*" required required-message={this.fieldMsg('company_name', 'requiredMessage', 'Please enter your Company name')}></me-input>

                    {(this.showJobTitle || this.showCompanySize) && (
                      <div class={`${COMPONENT_NAME}__row`}>
                        {this.showJobTitle && (
                          <me-input name="job_title" type="text" placeholder="Job title*" required required-message={this.fieldMsg('job_title', 'requiredMessage', 'Please enter your Job title')}></me-input>
                        )}
                        {this.showCompanySize && (
                          <me-select name="users" placeholder="Company size*" required required-message={this.fieldMsg('users', 'requiredMessage', 'Please enter your Company size')} options={options}></me-select>
                        )}
                      </div>
                    )}

                    {this.showMessage && (
                      <me-textarea name="question" placeholder="Questions or comments (optional)" rows={3} maxLength={1000}></me-textarea>
                    )}

                    {this.renderHiddenFields()}

                    <me-button class={`${COMPONENT_NAME}__submit`} tag="button" type="submit" variant="primary">
                      {this.submitText}
                    </me-button>

                    <div class={`${COMPONENT_NAME}__agreement`} innerHTML={this.agreementNote}></div>
                  </form-handler-type-1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
